import {OVERRIDES} from "./rules";
import {pushToDataLayerObject, waitForDataLayer} from "../adapters/dataLayerAdapter";
import {cleanPayload} from "../utils/cleanPayload";
import type {DataLayerEvent, EventName, EventPayloadMap, PushOptions} from "./types";

/**
 * Stores deduplication keys to prevent the same event from firing multiple times.
 */
const firedDedupeKeys = new Set<string>();

/**
 * Represents an event queued for an event group before it is resolved and pushed.
 */
type QueuedEvent<T extends EventName = EventName> = {
    eventName: T;
    payload?: EventPayloadMap[T] | null;
    options?: PushOptions;
    insertedAt: number;
};

const DEFAULT_BUFFER_MS = 50;
const DEFAULT_EVENT_GROUP_BUFFER_MS = 1500;

/**
 * Defines default buffering times for specific events to allow override resolution.
 */
const EVENT_BUFFER_MS: Partial<Record<EventName, number>> = {
    teasercard_select: 50,
    navigation: 0,
    meta_navigation: 0,
    breadcrumb: 0,
    topic_navigation: 0,
    click_logo: 0,
};

/**
 * Holds queued events grouped by event group ID.
 */
const eventQueueByGroupId = new Map<string, QueuedEvent[]>();

/**
 * Stores active timers used to flush event group queues.
 */
const flushTimersByEventGroupId = new Map<string, ReturnType<typeof setTimeout>>();

// Tracks the already fired winning event per event group to drop overridden events arriving later
const firedWinnerByEventGroupId = new Map<string, EventName>();

// Events that should be flushed immediately when pushed within a group
const IMMEDIATE_WINNER_EVENTS = new Set<EventName>([
    'teasercard_select',
    'navigation',
    'meta_navigation',
    'breadcrumb',
    'topic_navigation',
]);

// Force inclusion of empty payload object for specific events
const FORCE_EMPTY_PAYLOAD_EVENTS = new Set<EventName>([
    'click_logo',
    'filter_reset',
    'newsletter_subscribe',
    'donation_begin',
]);

/**
 * Builds a dataLayer event object following the GX / TRKKN data model.
 * Adds event metadata and attaches the payload under the event name key.
 */
function buildDataLayerObject<T extends keyof EventPayloadMap>(
    eventName: T,
    payload?: EventPayloadMap[T] | null,
    options?: PushOptions
): DataLayerEvent<T> {
    const cleaned = payload ? (cleanPayload(payload) as EventPayloadMap[T]) : undefined;
    const hasKeys = cleaned && Object.keys(cleaned as object).length > 0;
    const mustIncludeEmpty = FORCE_EMPTY_PAYLOAD_EVENTS.has(eventName as EventName);

    return {
        event: `gx.${eventName}`,
        event_name: eventName,
        event_source: options?.source ?? "source_code",
        ...((cleaned && (hasKeys || mustIncludeEmpty)) ? {[eventName]: cleaned} as any : {}),
    } as DataLayerEvent<T>;
}

/**
 * Pushes an event to the dataLayer after ensuring it exists.
 * Applies optional deduplication using the provided dedupeKey.
 */
async function pushToDataLayer<T extends keyof EventPayloadMap>(
    eventName: T,
    payload?: EventPayloadMap[T] | null,
    options?: PushOptions
): Promise<void> {
    await waitForDataLayer();

    const dedupeKey = options?.dedupeKey;
    if (dedupeKey) {
        if (firedDedupeKeys.has(dedupeKey)) return;
        firedDedupeKeys.add(dedupeKey);
    }

    pushToDataLayerObject(buildDataLayerObject(eventName, payload, options));
}

/**
 * Pick the winning event:
 * 1. keep latest per eventName
 * 2. remove overridden events
 * 3. if multiple remain, pick the newest
 */
function resolveWinningEvent(events: QueuedEvent[]): QueuedEvent | null {
    if (events.length === 0) return null;

    // Step 1: keep only latest event per eventName
    const latestEventByName = new Map<EventName, QueuedEvent>();
    for (const event of events) {
        latestEventByName.set(event.eventName, event);
    }

    const latestCandidates = Array.from(latestEventByName.values());
    const candidateNames = new Set(latestCandidates.map(e => e.eventName));
    const suppressedNames = new Set<EventName>();

    // Step 2: mark events overridden by other candidates
    for (const candidate of latestCandidates) {
        const overriddenNames = OVERRIDES[candidate.eventName] ?? [];
        for (const name of overriddenNames) {
            if (candidateNames.has(name)) {
                suppressedNames.add(name);
            }
        }
    }

    const winners = latestCandidates.filter(
        c => !suppressedNames.has(c.eventName)
    );

    if (winners.length === 0) return null;
    if (winners.length === 1) return winners[0];

    // Step 3: fallback → pick most recently inserted
    return winners.reduce((latest, current) =>
        current.insertedAt > latest.insertedAt ? current : latest
    );
}

/**
 * Clears and removes the active flush timer for a given event group.
 */
function clearEventGroupTimer(eventGroupId: string): void {
    const timer = flushTimersByEventGroupId.get(eventGroupId);
    if (!timer) return;

    clearTimeout(timer);
    flushTimersByEventGroupId.delete(eventGroupId);
}

/**
 * Resolves and sends the final event for an event group,
 * then clears the queue and associated timer.
 */
async function flushEventGroup(eventGroupId: string): Promise<void> {
    const queued = eventQueueByGroupId.get(eventGroupId);
    eventQueueByGroupId.delete(eventGroupId);

    clearEventGroupTimer(eventGroupId);

    if (!queued?.length) return;

    const winningEvent = resolveWinningEvent(queued);
    if (!winningEvent) return;

    // Remember the fired winner for this group to drop later overridden events
    firedWinnerByEventGroupId.set(eventGroupId, winningEvent.eventName as EventName);

    await pushToDataLayer(
        winningEvent.eventName,
        winningEvent.payload,
        winningEvent.options
    );
}

/**
 * Schedules a delayed flush of an event group queue.
 * Used to allow competing events to resolve before firing.
 */
function scheduleEventGroupFlush(eventGroupId: string, delayMs?: number): void {
    clearEventGroupTimer(eventGroupId);

    const ms =
        typeof delayMs === "number" && delayMs >= 0 ? delayMs : DEFAULT_BUFFER_MS;

    const timer = setTimeout(() => {
        void flushEventGroup(eventGroupId);
    }, ms);

    flushTimersByEventGroupId.set(eventGroupId, timer);
}

/**
 * Public entry point for pushing tracking events.
 * Handles optional event group buffering and override logic.
 */
export async function pushEvent<T extends keyof EventPayloadMap>(
    eventName: T,
    payload?: EventPayloadMap[T] | null,
    options?: PushOptions
): Promise<void> {
    // Events without eventGroupId are pushed immediately
    const eventGroupId = options?.eventGroupId;
    if (!eventGroupId) {
        await pushToDataLayer(eventName, payload, options);
        return;
    }

    // If a winner has already been fired for this group and that winner overrules
    // the incoming event, drop this event immediately.
    const alreadyFiredWinner = firedWinnerByEventGroupId.get(eventGroupId);
    if (alreadyFiredWinner) {
        const overriddenByWinner = (OVERRIDES[alreadyFiredWinner] ?? []) as EventName[];
        if (overriddenByWinner.includes(eventName as EventName)) {
            return; // suppressed by previously fired winner in this interaction
        }
    }

    // Create event entry and add it to the event group queue
    const queuedEvent: QueuedEvent<T> = {
        eventName,
        payload,
        options,
        insertedAt: Date.now(), // used to resolve ties (latest event wins)
    };

    const queue = eventQueueByGroupId.get(eventGroupId) ?? [];
    queue.push(queuedEvent);
    eventQueueByGroupId.set(eventGroupId, queue);

    // If this event is marked as immediate winner, flush the group right away
    if (IMMEDIATE_WINNER_EVENTS.has(eventName as EventName)) {
        await flushEventGroup(eventGroupId);
        return;
    }

    // If multiple events exist for this event group,
    // check if the new event clearly overrides the others
    if (queue.length > 1) {
        const winningEvent = resolveWinningEvent(queue);

        if (winningEvent === queuedEvent) {
            const namesInQueue = Array.from(new Set(queue.map((e) => e.eventName)));
            const competingEventNames = namesInQueue.filter(
                (name) => name !== queuedEvent.eventName
            );

            const overrides: EventName[] = OVERRIDES[queuedEvent.eventName] ?? [];

            const isClearWinner =
                competingEventNames.length > 0 &&
                competingEventNames.every((name) => overrides.includes(name));

            // If the event overrides all competing events, flush immediately
            if (isClearWinner) {
                await flushEventGroup(eventGroupId);
                return;
            }
        }
    }

    // Otherwise buffer the event group for a short time
    const effectiveBufferMs =
        typeof options?.bufferMs === "number"
            ? options.bufferMs
            : EVENT_BUFFER_MS[eventName] ?? DEFAULT_EVENT_GROUP_BUFFER_MS;

    scheduleEventGroupFlush(eventGroupId, effectiveBufferMs);
}
